<script setup lang="ts" generic="T">
import YscIconsArrowLeft from './YscIconsArrowLeft.vue'
import YscIconsArrowRight from './YscIconsArrowRight.vue'
import { computed, ref, defineModel } from '#imports'

interface Props {
  aspect?: string
  items: T[]
}

const props = withDefaults(defineProps<Props>(), {
  aspect: 'aspect-[16/9]',
  items: () => [],
})

const index = defineModel('startIndex', { type: Number, default: 0 })

const transitionDirection = ref<'slideshow-left' | 'slideshow-right'>('slideshow-left')

const move = (increment: number) => {
  transitionDirection.value = increment > 0 ? 'slideshow-right' : 'slideshow-left'
  let i: number = index.value + increment
  if (i < 0) {
    i = props.items.length - 1
  }
  else if (i >= props.items.length) {
    i = 0
  }
  index.value = i
}

const activeItem = computed(() => props.items[index.value])
const hasControls = computed(() => props.items.length > 1)
</script>

<template>
  <div
    :class="aspect"
    class="group/slideshow relative overflow-hidden bg-gray-200 text-white"
  >
    <transition :name="transitionDirection">
      <slot
        :active-item="activeItem"
        :transition-key="`slide-item-${index}`"
      />
    </transition>

    <template v-if="hasControls">
      <button
        type="button"
        :aria-label="$t('commons.previous')"
        class="absolute bottom-0 left-0 top-0 z-20 h-full w-12"
        @click.stop.prevent="move(-1)"
      >
        <span
          class="relative flex h-full w-full flex-col items-start justify-center bg-gradient-to-r from-black/30 pl-2 sm:hidden sm:group-hover/slideshow:flex"
        >
          <yscIconsArrowLeft class="h-6 w-6" />
        </span>
      </button>

      <div class="absolute bottom-2 flex w-full items-center justify-center space-x-1">
        <span
          v-for="(item, i) in items"
          :key="`bullet-${item}-${i}`"
          :class="[i === index ? 'h-2.5 w-2.5 bg-opacity-100' : 'h-2 w-2 bg-opacity-40']"
          class="rounded-full bg-white transition-all"
        />
      </div>

      <button
        type="button"
        :aria-label="$t('commons.next')"
        class="absolute bottom-0 right-0 top-0 z-20 h-full w-12"
        @click.stop.prevent="move(1)"
      >
        <span
          class="relative flex h-full w-full flex-col items-end justify-center bg-gradient-to-l from-black/30 pr-2 sm:hidden sm:group-hover/slideshow:flex"
        >
          <yscIconsArrowRight class="h-6 w-6" />
        </span>
      </button>
    </template>
  </div>
</template>

<style scoped>
.slideshow-right-enter-active{animation:slideRightIn .25s;animation-timing-function:cubic-bezier(.91,.08,.05,.97)}@keyframes slideRightIn{0%{transform:translateX(100%)}to{transform:translateX(0)}}.slideshow-right-leave-active{animation:slideRightOut .25s;animation-timing-function:cubic-bezier(.91,.08,.05,.97);@apply absolute inset-0}@keyframes slideRightOut{0%{transform:translateX(0)}to{transform:translateX(-100%)}}.slideshow-left-enter-active{animation:slideLeftIn .25s;animation-timing-function:cubic-bezier(.91,.08,.05,.97)}@keyframes slideLeftIn{0%{transform:translateX(-100%)}to{transform:translateX(0)}}.slideshow-left-leave-active{animation:slideLeftOut .25s;animation-timing-function:cubic-bezier(.91,.08,.05,.97);@apply absolute inset-0}@keyframes slideLeftOut{0%{transform:translateX(0)}to{transform:translateX(100%)}}
</style>
